import { isAxiosError } from 'axios';
import { getDownloadSignedUrl } from '~/api/projects2';
import { captureTypedPosthogEvent, POSTHOG_EVENTS } from '~/common/posthogEvents';
import { Notify } from '~/components2/Notify';

/**
 * Saving a download means handing a signed URL to the browser's own download manager, which streams
 * it to disk. Assembling the file in the page instead caps it at what a Blob can hold in the tab,
 * and an export archive runs past a gigabyte.
 *
 * The URL is minted by our own API on the click rather than read off the list row, so the request
 * that can be refused is one we can read a status from — the navigation itself reports nothing.
 *
 * Kept clear of the app store so the history table can be rendered without it.
 */
export type DownloadAnalytics = {
  projectId: string;
  organizationId?: string;
  fileId?: string;
  /** Falls back to the file's own extension when the caller has no format to name. */
  filetype?: string;
  source: string;
  submissionId?: string;
  exportTypes?: string[];
};

/** Names which of a row's two objects to sign: its own format file, or the run's combined archive. */
export type DownloadTarget = {
  projectId: string;
  downloadId: string;
  target: 'file' | 'combined';
};

const reasonForError = (e: unknown) => (isAxiosError(e) && e.response ? String(e.response.status) : 'network_error');

/**
 * Report a download the browser was never handed a file for, so a missing object, a revoked
 * permission and a dropped connection reach the user and the funnel rather than only the console.
 */
const reportDownloadFailure = (reason: string, analytics: DownloadAnalytics, filename: string, filetype: string) => {
  Notify.error({
    title: 'Download failed',
    message: 'There was an error downloading that file. Please try again, or contact support if it keeps happening.',
  });

  captureTypedPosthogEvent(POSTHOG_EVENTS.projectFileDownloadFailed, {
    project_id: analytics.projectId,
    organization_id: analytics.organizationId,
    file_id: analytics.fileId,
    file_name: filename,
    file_type: filetype,
    delivery: 'browser',
    source: analytics.source,
    submission_id: analytics.submissionId,
    export_types: analytics.exportTypes,
    reason,
  });
};

export const saveExportDownload = async (target: DownloadTarget, analytics: DownloadAnalytics) => {
  // Only the signing request can fail the download. Anything after it runs outside the catch, so a
  // throw there cannot report a hand-off that already happened as a failure.
  const signed = await getDownloadSignedUrl(target.projectId, target.downloadId, target.target).catch((e: unknown) => {
    reportDownloadFailure(reasonForError(e), analytics, '', analytics.filetype || 'unknown');
    return null;
  });

  if (!signed) return;

  const filetype = analytics.filetype || signed.filename.split('.').pop()?.toLowerCase() || 'unknown';

  // The URL is signed with `Content-Disposition: attachment`, so a same-tab navigation starts the
  // download and leaves the page where it is.
  window.location.href = signed.url;

  // Counts a hand-off the server has confirmed and signed for, not a finished save.
  captureTypedPosthogEvent(POSTHOG_EVENTS.projectFileDownloaded, {
    project_id: analytics.projectId,
    organization_id: analytics.organizationId,
    file_id: analytics.fileId,
    file_name: signed.filename,
    file_type: filetype,
    delivery: 'browser',
    source: analytics.source,
    submission_id: analytics.submissionId,
    export_types: analytics.exportTypes,
  });
};

/** Downloads the archive holding every format an export run produced. */
export const downloadExportRun = (run: {
  download: Omit<DownloadTarget, 'projectId'> | null;
  projectId: string;
  organizationId: string;
  submissionId: string | null;
  formats: { format: string | null }[];
}) => {
  if (!run.download) return Promise.resolve();

  return saveExportDownload(
    { projectId: run.projectId, ...run.download },
    {
      projectId: run.projectId,
      organizationId: run.organizationId,
      source: 'export_history_run',
      submissionId: run.submissionId ?? undefined,
      exportTypes: run.formats.flatMap((entry) => (entry.format ? [entry.format.toLowerCase()] : [])),
    },
  );
};
